import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { message, Modal, Typography, List, Tag, Row, Col, Space, Card, Descriptions } from "antd";
import { connect } from "react-redux";
import axios from "axios";
import { ModalItem, ButtonItem, dineroDisabledMask } from "../../../../Components/Items";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  TruckOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { STATUS_CONFIG } from "../statusPaqueteConfig";
import { ORIGEN_CONFIG } from "../../../../Components/VentaOnlineCard";
import { TIPO_ENVIO_LABELS } from "../useVentasEcommerceAcciones";

const { Text, Title } = Typography;

const ModalEstadoGeneralPedido = ({
  visible,
  ventaId,
  empresaId,
  headersToken,
  onCancel,
  permitirBajaLotesReemplazoVentas,
}) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [venta, setVenta] = useState(null);
  const [reemplazando, setReemplazando] = useState(null);

  const cargarVenta = () => {
    setLoading(true);
    return axios
      .post(
        "ventasEcommerce/obtenerVentaPorId",
        { ventaId, empresaId },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          setVenta(res.data.venta);
        } else {
          message.warning(res.data.message || "No se pudo obtener el pedido.");
        }
      })
      .catch(() => message.error("Error al obtener el detalle del pedido"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (visible && ventaId) {
      cargarVenta();
    }
    if (!visible) {
      setVenta(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, ventaId, empresaId, headersToken]);

  // Le quita a la venta un codigo cuyo lote ya se dio de baja, le entrega otro y le manda el
  // correo de codigos (lotes/reemplazarLoteVenta, Controllers/bajaLotes.controllers.js).
  const reemplazarCodigo = (codigo) => {
    Modal.confirm({
      title: `¿Reemplazar el código ${codigo.numero}?`,
      content: "Se le quita a este pedido, se le entrega el siguiente código disponible y se le manda el correo con el código nuevo.",
      okText: "Reemplazar",
      cancelText: "Cancelar",
      onOk: () => {
        setReemplazando(String(codigo.entradaId));
        return axios
          .post(
            "lotes/reemplazarLoteVenta",
            { empresaId, ventaId, entradaId: codigo.entradaId },
            { headers: headersToken },
          )
          .then((res) => {
            if (res.data.success === 2) {
              const nuevos = (res.data.codigosNuevos || []).join(", ");
              message.success(
                `${res.data.message}${nuevos ? ` Código nuevo: ${nuevos}.` : ""}${
                  res.data.correoEnviado ? " Correo enviado." : res.data.motivoCorreo ? ` Correo no enviado: ${res.data.motivoCorreo}` : ""
                }`,
                6,
              );
              return cargarVenta();
            }
            message.error(res.data.message || "No se pudo reemplazar el código.", 6);
          })
          .catch(() => message.error("Error de comunicación con el servidor"))
          .finally(() => setReemplazando(null));
      },
    });
  };

  const irAEntregarInsumos = () => {
    onCancel();
    history.push("/panel/entregarInsumosVenta", { id: ventaId });
  };

  const insumosFiltrados = (venta?.insumos || []).filter((ins) => !ins.servicio);

  const hayInsumosParaEntregar = insumosFiltrados.some(
    (ins) => !ins.dropshipping && !ins.libroVirtual,
  );

  return (
    <ModalItem
      title="Estado general del pedido"
      visible={visible}
      onCancel={onCancel}
      width={800}
      spinning={loading}
    >
      {venta && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Space size={16} wrap>
              <Tag color="blue" style={{ fontSize: 14, padding: "2px 8px" }}>
                Folio: {venta.serieNumero}
              </Tag>
              <Title level={5} style={{ margin: 0, color: "#333" }}>
                <UserOutlined /> {venta.clienteOnline?.nombre}{" "}
                {venta.clienteOnline?.apellidoPaterno}
              </Title>
              <Text type="secondary">
                <MailOutlined style={{ marginRight: 5 }} /> {venta.clienteOnline?.email}
              </Text>
              <Text type="secondary">
                <PhoneOutlined style={{ marginRight: 5 }} /> {venta.clienteOnline?.telefono}
              </Text>
            </Space>
          </div>

          {(venta.cotizacionesEnvioOnline || []).map((envio) => {
            const origenCfg = ORIGEN_CONFIG[envio.origen] || ORIGEN_CONFIG.bodega_local;
            const estadoCfg = STATUS_CONFIG[envio.estadoPaquete] || STATUS_CONFIG[0];
            const isPickup = envio.origen === "entrega_sucursal";
            const lineasLibroVirtual = (venta.insumos || []).filter((ins) => ins.libroVirtual);
            const codigosLibroVirtual = lineasLibroVirtual.flatMap(
              (ins) => ins.codigosLibroVirtual || [],
            );
            const hayErrorLibroVirtual = lineasLibroVirtual.some(
              (ins) => ins.libroVirtualError,
            );

            return (
              <Card
                key={envio.origen}
                size="small"
                style={{
                  marginBottom: 16,
                  borderRadius: 10,
                  borderLeft: `5px solid ${origenCfg.color}`,
                }}
                title={
                  <Space>
                    <Tag color={origenCfg.antd}>
                      {TIPO_ENVIO_LABELS[envio.origen] || origenCfg.label}
                    </Tag>
                    <Tag color={estadoCfg.color} icon={estadoCfg.icon}>
                      {estadoCfg.label}
                    </Tag>
                  </Space>
                }
              >
                <Descriptions column={2} size="small" bordered>
                  {isPickup ? (
                    <Descriptions.Item label="Entrega" span={2}>
                      {envio.pickupBranch?.label || "Recoger en sucursal"}
                    </Descriptions.Item>
                  ) : (
                    <>
                      <Descriptions.Item label="Paquetería">
                        {envio.carrier?.toUpperCase() || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Servicio">
                        {envio.service || "-"}
                      </Descriptions.Item>
                      {envio.trackingNumber && (
                        <Descriptions.Item label="Rastreo" span={2}>
                          {envio.trackingNumber}
                        </Descriptions.Item>
                      )}
                      {envio.folioRecoleccion && (
                        <Descriptions.Item label="N° Recolección" span={2}>
                          {envio.folioRecoleccion}
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Costo de envío cotizado">
                        {dineroDisabledMask(envio.costoApi)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Cobro de envío al cliente">
                        {dineroDisabledMask(envio.costoCliente)}
                      </Descriptions.Item>
                      {envio.costoEtiqueta > 0 && (
                        <Descriptions.Item label="Costo de envío real (facturado)" span={2}>
                          {dineroDisabledMask(envio.costoEtiqueta)}
                        </Descriptions.Item>
                      )}
                    </>
                  )}
                </Descriptions>

                <Row gutter={16} style={{ marginTop: 12 }}>
                  <Col span={24}>
                    <Text strong>Total de la venta: </Text>
                    <Text strong style={{ color: origenCfg.color, fontSize: 15 }}>
                      {dineroDisabledMask(venta.total)}
                    </Text>
                  </Col>
                </Row>

                {lineasLibroVirtual.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Space size={6} wrap>
                      {codigosLibroVirtual.length > 0 && (
                        <Tag color="purple">
                          Código Virtual: {codigosLibroVirtual.map((c) => c.numero).join(", ")}
                        </Tag>
                      )}
                      {hayErrorLibroVirtual && (
                        <Tag color="red">Código pendiente de asignar</Tag>
                      )}
                    </Space>
                  </div>
                )}
              </Card>
            );
          })}

          <List
            header={
              <Text strong style={{ fontSize: 15 }}>
                Estado de insumos
              </Text>
            }
            bordered
            dataSource={insumosFiltrados}
            style={{ background: "white", borderRadius: 8, marginBottom: 16 }}
            renderItem={(item) => {
              const entregado = item.entregado || 0;
              const pendiente = entregado < item.cantidad;
              return (
                <List.Item>
                  <List.Item.Meta
                    title={<Text strong>{item.descripcion}</Text>}
                    description={
                      <Space size={6} wrap>
                        <Tag color="default">Cant: {item.cantidad}</Tag>
                        {item.libroVirtual ? (
                          <>
                            <Tag color="purple">Código Virtual</Tag>
                            <Tag color={item.libroVirtualError ? "red" : "green"}>
                              {item.libroVirtualError
                                ? "Código pendiente de asignar"
                                : "Código asignado"}
                            </Tag>
                            {permitirBajaLotesReemplazoVentas && (item.codigosLibroVirtual || []).map((codigo) => (
                              <Space key={String(codigo.entradaId || codigo.numero)} size={4}>
                                <Tag color={codigo.loteDadoDeBaja ? "red" : "default"}>
                                  {codigo.numero}
                                  {codigo.loteDadoDeBaja ? " · Lote dado de baja" : ""}
                                </Tag>
                                {codigo.loteDadoDeBaja && (
                                  <ButtonItem
                                    size="small"
                                    naranja
                                    icon={<SwapOutlined />}
                                    loading={reemplazando === String(codigo.entradaId)}
                                    disabled={reemplazando !== null}
                                    onClick={() => reemplazarCodigo(codigo)}
                                  >
                                    Reemplazar código
                                  </ButtonItem>
                                )}
                              </Space>
                            ))}
                          </>
                        ) : (
                          <>
                            <Tag color={item.empaquetado ? "green" : "orange"}>
                              {item.empaquetado ? "Empaquetado" : "Sin empaquetar"}
                            </Tag>
                            <Tag color={pendiente ? "red" : "green"}>
                              Entregado: {entregado} / {item.cantidad}
                            </Tag>
                          </>
                        )}
                        {item.dropshipping && <Tag color="cyan">Dropshipping</Tag>}
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />

          {hayInsumosParaEntregar && (
            <div style={{ textAlign: "right" }}>
              <ButtonItem icon={<TruckOutlined />} morado onClick={irAEntregarInsumos}>
                Ir a Entregar Insumos
              </ButtonItem>
            </div>
          )}
        </>
      )}
    </ModalItem>
  );
};

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  permitirBajaLotesReemplazoVentas: state.empresa.permitirBajaLotesReemplazoVentas,
});

export default connect(mapStateToProps)(ModalEstadoGeneralPedido);
